import {
	mdiAt,
	mdiBellBadge,
	mdiDomain,
	mdiEmailNewsletter,
	mdiEmailSeal, mdiFileSign,
	mdiFormatListBulleted,
	mdiLink,
	mdiOrderBoolAscendingVariant,
	mdiPhone,
} from "@mdi/js";

import { RawDraftContentState } from "draft-js";
import { i18n } from "@lingui/core";

import { Account, AccountType } from "models";

export enum WorkflowObjectType
{
	GENERIC = "GENERIC",
	ASSET = "ASSET",
	MATCH = "MATCH",
	CONTRACT = "CONTRACT"
}

export interface Template
{
	id: string
	name: string
	description: string
	// deleted: boolean
	system: boolean
	defaultWorkflow: boolean
	creationDate: string // date
	objectType: WorkflowObjectType
	creator: Account
	defaultExecutor: Account
}

export interface TemplateStep
{
	id: string
	name: string
	creationDate: string // date
	order: number
	deadlineDays: number
	actionType: WorkflowStepType
	content: RawDraftContentState
	creator: Account
	defaultExecutor: Account
}

export enum WorkflowStatus {
	WORKING = "WORKING",
	DONE = "DONE"
}
export interface Workflow
{
	id: string
	name: string
	description: string
	// deleted: boolean
	creationDate: string  // date
	objectType: WorkflowObjectType
	status: WorkflowStatus
	doneDate: string  // date
	creator: Account
	executor: Account
	linkedItem: string
}

export interface Action {
	id: string
	name: string
	creationDate: string // ISO date
	executionDate: string | null
	order: number
	status: ActionStatus
	actionType: WorkflowStepType
	deadline: string
	content: RawDraftContentState
	oldContent: RawDraftContentState | {}
	workflow: Workflow | null
	creator: Account | null
	executor: Account | null
	assignedActorRole: AccountType | null
}


export interface WorkflowTemplateSteps
{
	steps: TemplateStep[]
}

export interface ActionTimeline
{
	id: string
	action: Action
	type: ActionTimelineType
	status: ActionStatus
	creationDate: string // date
	oldExecutor: Account
	newExecutor: Account
	oldDeadline: string // date
	newDeadline: string // date
}

export interface NewWorkflowTemplate
{
	name: string,
	description: string | null,
	workflowObjectType: string,
	defaultExecutor: string | null
}

export interface NewStepRequest
{
	name: string,
	actionType: string,
	order: number,
	deadlineDays: number,
	executor: string | null,
	content: RawDraftContentState
}

export interface WorkflowActionsResponse
{
	actions: Action[]
}

export interface NewWorkflowAction
{
	name: string
	actionType: string
	deadlineDays: number
	executor: string
	content: RawDraftContentState
}

export class ActionsRequest
{
	pageSize: number = 10
	pageId: number = 0
	orderType: string | null = null
	orderBy: string | null = null
	executor: string | null = null
	statusList: string[] | null = null
	type: string | null = null
	executionStartDate: Date | null = null
	executionEndDate: Date | null = null

	public static empty(): ActionsRequest
	{
		return new ActionsRequest()
	}
}

export interface CreateSpecificWorkflow {
	objectId: string
	objectType: WorkflowObjectType
	workflowTemplateId: string
}

export enum ActionStatus
{
	PENDING = "PENDING", //
	WORKING = "WORKING", //
	DONE = "DONE", //
	SKIPPED = "SKIPPED", //
}

export class ActionStatusTranslations
{
	PENDING = /*i18n*/i18n._({id: "enums.action-status.PENDING", message: "In attesa"})
	WORKING = /*i18n*/i18n._({id: "enums.action-status.WORKING", message: "In lavorazione"})
	DONE = /*i18n*/i18n._({id: "enums.action-status.DONE", message: "Eseguita"})
	EXPIRED = /*i18n*/i18n._({id: "enums.action-status.EXPIRED", message: "Scaduta"})
	SKIPPED = /*i18n*/i18n._({id: "enums.action-status.SKIPPED", message: "Saltata"})
}

export enum WorkflowStepType
{
	NOTIFICATION = "NOTIFICATION", //
	EMAIL = "EMAIL", //
	PEC = "PEC", //
	PHONE_CALL = "PHONE_CALL", //
	LETTER = "LETTER", //
	MANUAL_ACTION = "MANUAL_ACTION", //
}
export class WorkflowStepTypeTranslations
{
	NOTIFICATION = /*i18n*/i18n._({id: "enums.workflow-step-types.NOTIFICATION", message: "Notifica"})
	EMAIL = /*i18n*/i18n._({id: "enums.workflow-step-types.EMAIL", message: "E-Mail"})
	PEC = /*i18n*/i18n._({id: "enums.workflow-step-types.PEC", message: "PEC"})
	PHONE_CALL = /*i18n*/i18n._({id: "enums.workflow-step-types.PHONE_CALL", message: "Telefonata"})
	LETTER = /*i18n*/i18n._({id: "enums.workflow-step-types.LETTER", message: "Lettera"})
	MANUAL_ACTION = /*i18n*/i18n._({id: "enums.workflow-step-types.MANUAL_ACTION", message: "Azione manuale"})
}

export enum ActionTimelineType
{
	CREATE = "CREATE",
	WORK = "WORK",
	ASSIGN = "ASSIGN",
	DONE = "DONE",
	DEFER = "DEFER",
	SKIP = "SKIP",
	EXPIRE = "EXPIRE",
}

export class ActionTimelineTypeTranslations {
	CREATE =  /*i18n*/i18n._({id: "enums.action-timeline-type.CREATE", message: "Azione creata"})
	WORK = /*i18n*/i18n._({id: "enums.action-timeline-type.WORK", message: "Azione eseguibile"})
	ASSIGN = /*i18n*/i18n._({id: "enums.action-timeline-type.ASSIGN", message: "Azione assegnata"})
	DONE = /*i18n*/i18n._({id: "enums.action-timeline-type.DONE", message: "Azione eseguita"})
	DEFER = /*i18n*/i18n._({id: "enums.action-timeline-type.DEFER", message: "Azione posticipata"})
	SKIP = /*i18n*/i18n._({id: "enums.action-timeline-type.SKIP", message: "Azione saltata"})
	EXPIRE = /*i18n*/i18n._({id: "enums.action-timeline-type.EXPIRE", message: "Azione scaduta"})
}

export interface UpdateActionRequest {
	content: RawDraftContentState
	name: string
	actionType: WorkflowStepType
}

export class ActionUtils
{
	public static renderIcon = (action: Action): string => {
		switch(action.actionType) {
			case WorkflowStepType.NOTIFICATION:
				return mdiBellBadge
			case WorkflowStepType.EMAIL:
				return mdiAt
			case WorkflowStepType.PEC:
				return mdiEmailSeal
			case WorkflowStepType.PHONE_CALL:
				return mdiPhone
			case WorkflowStepType.LETTER:
				return mdiEmailNewsletter
			case WorkflowStepType.MANUAL_ACTION:
				return mdiOrderBoolAscendingVariant
		}
	}

	public static renderObject = (type: string): WorkflowStepType|null => {
		if (type === "NOTIFICATION" || type === "EMAIL" || type === "PEC" || type === "PHONE_CALL" || type === "LETTER" || type === "MANUAL_ACTION"){
			switch(type) {
				case "NOTIFICATION":
					return WorkflowStepType.NOTIFICATION
				case "EMAIL":
					return WorkflowStepType.EMAIL
				case "PEC":
					return WorkflowStepType.PEC
				case "PHONE_CALL":
					return WorkflowStepType.PHONE_CALL
				case "LETTER":
					return WorkflowStepType.LETTER
				case "MANUAL_ACTION":
					return WorkflowStepType.MANUAL_ACTION
			}
		}
		return null
	}

	public static renderString = (type: WorkflowStepType): string => {
		switch(type) {
			case WorkflowStepType.NOTIFICATION:
				return "NOTIFICATION"
			case WorkflowStepType.EMAIL:
				return "EMAIL"
			case WorkflowStepType.PEC:
				return "PEC"
			case WorkflowStepType.PHONE_CALL:
				return "PHONE_CALL"
			case WorkflowStepType.LETTER:
				return "LETTER"
			case WorkflowStepType.MANUAL_ACTION:
				return "MANUAL_ACTION"
		}
	}
}


export class WorkflowUtils
{
	public static renderIcon = (objectType: WorkflowObjectType): string => {
		switch(objectType) {
			case WorkflowObjectType.GENERIC:
				return mdiFormatListBulleted
			case WorkflowObjectType.MATCH:
				return mdiLink
			case WorkflowObjectType.ASSET:
				return mdiDomain
			case WorkflowObjectType.CONTRACT:
				return mdiFileSign
		}
	}
}

export class WorkflowActionUtils
{
	public static renderIcon = (actionType: WorkflowStepType): string => {
		switch(actionType) {
			case WorkflowStepType.NOTIFICATION:
				return mdiBellBadge
			case WorkflowStepType.EMAIL:
				return mdiAt
			case WorkflowStepType.PEC:
				return mdiEmailSeal
			case WorkflowStepType.PHONE_CALL:
				return mdiPhone
			case WorkflowStepType.LETTER:
				return mdiEmailNewsletter
			case WorkflowStepType.MANUAL_ACTION:
				return mdiOrderBoolAscendingVariant
			default:
				return ""
		}
	}
}

export interface AssignRequest {
	executor: string
}
